import { computed, onUnmounted, watch, type ComputedRef } from 'vue'
import type { FederatedLabHit } from '~/utils/mapFederatedSearchLabResponse'
import { HITS_PER_PAGE } from '~/components/FederatedSearch/helpers'
import { getFederatedSearchItemListIds } from '~/components/FederatedSearch/gtmItemLists'
import {
  sendSearchPanelOpen,
  sendViewItemList,
} from '~/composables/useGtmEvents'
import type { SearchAnalyticsParams } from '~/utils/searchAnalytics'
import type { SearchEngineEventItem } from '~/composables/types/searchEngine'
import type { EventDescription } from '~/composables/types/event'
import type { FederatedSearchAnalyticsPlacement } from '~/utils/searchEngineHelpers'
import {
  getVisibleTypesenseEventItems,
  isTypesenseEventsColumnVisible,
} from './gtmItemLists'

interface ItemListViewsSource {
  events: FederatedLabHit[]
  lastViewedEvents: EventDescription[]
  query: string
  resultsQuery: string | null
  isLoading: boolean
  isLastViewedReady: boolean
  maxEvents?: number
  placement?: FederatedSearchAnalyticsPlacement
  /** Placement, city and content group for `search_panel_open` (KFT-4280). */
  searchAnalytics?: Partial<SearchAnalyticsParams>
  session: ItemListViewSession
}

/**
 * What was already sent during the current panel open. Owned by the parent
 * FederatedSearchTypesense (not the panel): its Clear button makes headlessui
 * close and reopen the panel, which must not count as a new open — the
 * Algolia panel stays open on clear.
 */
export interface ItemListViewSession {
  lastResultsViewQuery: string | null
  hasSentLastViewedView: boolean
  hasSentPanelOpen: boolean
  /** Set while the parent closes + reopens the panel itself (Clear). */
  isHeld: boolean
}

export const createItemListViewSession = (): ItemListViewSession => ({
  lastResultsViewQuery: null,
  hasSentLastViewedView: false,
  hasSentPanelOpen: false,
  isHeld: false,
})

const resetItemListViewSession = (session: ItemListViewSession) => {
  session.lastResultsViewQuery = null
  session.hasSentLastViewedView = false
  session.hasSentPanelOpen = false
}

/**
 * GA4 `view_item_list` for the Typesense federated panel, ported from the
 * Algolia FederatedSearch/index.vue (not per keystroke):
 * - event results: once per settled query (1200ms), only while the events
 *   column is visible — with an empty query it is replaced by last viewed.
 * - last viewed: once per panel open, when that column is shown.
 * Both carry the query as `search_term` ('' before typing). Plus the custom
 * `search_panel_open` once per open (KFT-4280).
 *
 * Call from FederatedSearchTypesensePanel: headlessui only mounts the panel
 * while the dropdown / overlay is open, so its unmount is the close that
 * resets the session (unless the parent holds it, see ItemListViewSession).
 */
export const useTypesenseFederatedItemListViews = (
  source: ComputedRef<ItemListViewsSource>
) => {
  const itemListIds = computed(() =>
    getFederatedSearchItemListIds(source.value.placement)
  )

  let pendingResultsViewQuery: string | null = null
  let resultsViewTimer: ReturnType<typeof setTimeout> | null = null

  const cancelPendingResultsView = () => {
    if (resultsViewTimer) {
      clearTimeout(resultsViewTimer)
      resultsViewTimer = null
    }
    pendingResultsViewQuery = null
  }

  const isEventsColumnVisible = () =>
    isTypesenseEventsColumnVisible(source.value)

  // The shown results belong to the typed query
  const isSettled = () => {
    const { query, resultsQuery, isLoading } = source.value
    return !isLoading && resultsQuery !== null && resultsQuery === query
  }

  const trackResultsView = () => {
    const { events, query, maxEvents, session } = source.value
    if (!isSettled()) {
      // Moved on to another query: the pending view is for results that are
      // no longer what the user is looking at
      if (pendingResultsViewQuery !== query.trim()) cancelPendingResultsView()
      return
    }
    const settledQuery = query.trim()
    const items = getVisibleTypesenseEventItems(events, maxEvents)
    if (!isEventsColumnVisible() || !items.length) {
      if (pendingResultsViewQuery === settledQuery) cancelPendingResultsView()
      return
    }
    if (
      settledQuery === session.lastResultsViewQuery ||
      settledQuery === pendingResultsViewQuery
    ) {
      return
    }
    cancelPendingResultsView()
    pendingResultsViewQuery = settledQuery
    resultsViewTimer = setTimeout(() => {
      resultsViewTimer = null
      pendingResultsViewQuery = null
      // Re-check: the query may have changed, or last viewed may have loaded
      // and hidden the column meanwhile
      if (
        !isSettled() ||
        source.value.query.trim() !== settledQuery ||
        !isEventsColumnVisible()
      ) {
        return
      }
      session.lastResultsViewQuery = settledQuery
      // Typesense event docs are Algolia-shaped (KFT-4024)
      sendViewItemList(
        itemListIds.value.results,
        items as unknown as SearchEngineEventItem[],
        { searchTerm: settledQuery }
      )
    }, 1200)
  }

  const trackLastViewedView = () => {
    const { lastViewedEvents, query, session } = source.value
    if (session.hasSentLastViewedView || query.trim()) return
    const events = lastViewedEvents.slice(0, HITS_PER_PAGE.lastViewed)
    if (!events.length) return
    session.hasSentLastViewedView = true
    sendViewItemList(itemListIds.value.lastViewed, events, { searchTerm: '' })
  }

  const trackPanelOpen = () => {
    const { query, searchAnalytics, placement, session } = source.value
    if (session.hasSentPanelOpen) return
    session.hasSentPanelOpen = true
    sendSearchPanelOpen(query, {
      search_placement: placement ?? '',
      ...searchAnalytics,
    })
  }

  watch(
    source,
    () => {
      trackPanelOpen()
      trackResultsView()
      trackLastViewedView()
    },
    { immediate: true }
  )

  onUnmounted(() => {
    cancelPendingResultsView()
    const { session } = source.value
    if (!session.isHeld) resetItemListViewSession(session)
  })
}
